// src/pages/BlogPostPage.jsx - WITH DEBUGGING

import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useBlogPost } from '../hooks/useBlogPost';
import BlogContent from '../components/blog/BlogContent';
import BlogAuthorBio from '../components/blog/BlogAuthorBio';
import BlogShareButtons from '../components/blog/BlogShareButtons';
import BlogTableOfContents from '../components/blog/BlogTableOfContents';
import BlogRelatedPosts from '../components/blog/BlogRelatedPosts';
import BlogPostSkeleton from '../components/blog/BlogPostSkeleton';
import Banner from '../components/ads/Banner';
import { formatDate, getCategoryName, getBlogImageUrl } from '../utils/blogUtils';

const BlogPostPage = () => {
  const { slug } = useParams(); // Get slug from URL
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { post, relatedPosts, loading, error, structuredData, liked, likesCount, toggleLike, banners } = useBlogPost(slug);

  
  // Debug logging
  useEffect(() => {
    console.log('BlogPostPage - URL Params:', { slug });
    console.log('BlogPostPage - Post state:', post);
    console.log('BlogPostPage - Loading state:', loading);
    console.log('BlogPostPage - Error state:', error);
  }, [slug, post, loading, error]);
  
  // Set up meta tags for SEO (assuming you have a Helmet or similar)
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | DOROTS Blog`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', post.excerpt || post.title);
      }
    }
  }, [post]);

  // Handle errors (404, etc.)
  useEffect(() => {
    if (error && error.includes('404')) {
      console.error('Post not found, redirecting to 404');
      navigate('/404', { replace: true });
    }
  }, [error, navigate]);

  // Show loading state
  if (loading) {
    return <BlogPostSkeleton isDark={isDark} />;
  }

  // Show error state
  if (error || !post) {
    return (
      <div className={`pt-20 min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😕</div>
          <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Post Not Found
          </h2>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            The blog post you're looking for doesn't exist or has been moved.
          </p>
          <p className="text-sm mt-2 text-gray-500">
            Debug: Slug "{slug}" was requested
          </p>
          <Link
            to="/blog"
            className="inline-block mt-6 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-700 to-blue-600 text-white font-semibold hover:scale-105 transition-all"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <BlogTableOfContents content={post.content} isDark={isDark} />
      
      <article className={`py-12 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 transition-colors group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Blog
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                isDark 
                  ? 'bg-blue-500/20 text-blue-300' 
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {getCategoryName(post.category)}
              </span>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {post.reading_time || '5'} min read
              </span>
            </div>
            
            <h1 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {post.title}
            </h1>
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-700 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                {post.author_name?.charAt(0) || '👤'}
              </div>
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {post.author_name || 'Anonymous'}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {post.author_role || 'Contributor'} • {formatDate(post.published_at)}
                </p>
              </div>
            </div>
          </div>

          {post.featured_image && (
            <div className="rounded-2xl overflow-hidden mb-8 shadow-xl">
              <img 
                src={getBlogImageUrl(post.featured_image, post.title)}
                alt={post.title}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
          )}

          <BlogContent content={post.content} isDark={isDark} />
          {banners.length > 0 && (<Banner banner={banners[0]} post={post} />)}
          <div className={`mt-10 mb-10 flex items-center gap-4 border-t border-b py-6 ${isDark? 'border-white/10' : 'border-gray-200'}`}>
            <button
              onClick={toggleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all
                ${liked ? 'bg-blue-600 text-white' : 
                  isDark? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
              <span>
                {liked ? '👍' : '👍'}
              </span>

              <span>
                Helpful ({likesCount})
              </span>
            </button>

            <span className={isDark ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>
              Was this article helpful?
            </span>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <Link
                  key={tag.id || tag}
                  to={`/blog?tag=${typeof tag === 'string' ? tag : tag.slug}`}
                  className={`px-3 py-1 rounded-full text-sm transition-all hover:bg-gradient-to-r hover:from-blue-700 hover:to-blue-600 hover:text-white ${
                    isDark 
                      ? 'bg-white/10 text-gray-300' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  #{typeof tag === 'string' ? tag : tag.name}
                </Link>
              ))}
            </div>
          )}

          {post.author_name && (
            <BlogAuthorBio 
              author={{
                name: post.author_name,
                role: post.author_role,
                bio: post.author_bio,
                avatar: post.author_avatar
              }}
              isDark={isDark}
            />
          )}

          <BlogShareButtons 
            title={post.title}
            url={typeof window !== 'undefined' ? window.location.href : ''}
            isDark={isDark}
          />

          {relatedPosts.length > 0 && (
            <BlogRelatedPosts 
              posts={relatedPosts}
              isDark={isDark}
              onImageError={() => {}}
            />
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;